'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import ConnectionStatus from '@/components/ConnectionStatus';
import { DerivConnect } from '@/components/DerivConnect';
import ModeIndicator from '@/components/ModeIndicator';
import { useTradingData } from '@/hooks/useTradingData';
import { useAuth } from '@/hooks/useAuth';
import {
  Play,
  Square,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  Award,
  Zap,
  BarChart2,
  ListFilter,
  History,
  Settings,
  Terminal,
  LogOut,
  Cpu,
  Bot,
  ExternalLink,
  Link2,
} from 'lucide-react';

export default function DashboardPage() {
  const { user, loading: authLoading, logout } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (!authLoading && !user) {
      router.replace('/login');
    }
  }, [authLoading, user, router]);

  const {
    settings,
    accountBalance,
    todayPnL,
    winRate,
    tradeCount,
    openPositions,
    currentSignal,
    botStatus,
    trades,
    botLogs,
    derivAccount,
    startBot,
    stopBot,
    triggerEmergencyStop,
    closePosition,
    updateSettings,
  } = useTradingData();

  const [activeTab, setActiveTab] = useState<'dashboard' | 'trades' | 'signals' | 'settings' | 'logs'>('dashboard');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [botError, setBotError] = useState<string | null>(null);

  const isRunning = botStatus === 'running';
  const isPnLPositive = todayPnL >= 0;

  const handleStartStop = async () => {
    setIsProcessing(true);
    setBotError(null);
    if (isRunning) {
      await stopBot();
    } else {
      const err = await startBot();
      if (err) setBotError(err);
    }
    setIsProcessing(false);
  };

  const handleEmergency = async () => {
    if (window.confirm('EMERGENCY STOP: This will instantly halt all trading and close active positions. Continue?')) {
      setIsProcessing(true);
      await triggerEmergencyStop();
      setIsProcessing(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center font-sans">
        <div className="flex flex-col items-center gap-3">
          <Bot className="w-10 h-10 text-emerald-400 animate-pulse" />
          <p className="text-slate-400 text-sm">Loading Moah AI Trader...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col font-sans">
      {/* Top Header Navigation */}
      <header className="sticky top-0 z-40 bg-slate-900/90 backdrop-blur-md border-b border-slate-800/80 px-4 py-3">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-4">
          {/* Logo & Title */}
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-500/20 via-cyan-500/20 to-blue-500/20 border border-slate-700">
              <Bot className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="text-lg font-extrabold bg-gradient-to-r from-emerald-400 via-cyan-300 to-blue-400 bg-clip-text text-transparent">
                  Moah AI Trader
                </h1>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-cyan-400 font-mono">
                  PRO
                </span>
              </div>
              <p className="text-xs text-slate-400 hidden sm:block">
                AI Automated Deriv Algorithmic Execution
              </p>
            </div>
          </div>

          {/* Status & Indicators */}
          <div className="flex items-center space-x-3">
            <ModeIndicator />
            <ConnectionStatus />

            {/* Logout / User */}
            {user && (
              <button
                onClick={() => logout()}
                className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-slate-200 transition-colors"
                title={`Logged in as ${user.email}`}
              >
                <LogOut className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="max-w-7xl mx-auto flex items-center space-x-1 mt-3 pt-2 border-t border-slate-800/50 overflow-x-auto text-xs">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              activeTab === 'dashboard'
                ? 'bg-slate-800 text-cyan-400 border border-slate-700'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
            }`}
          >
            <BarChart2 className="w-3.5 h-3.5" />
            <span>Dashboard</span>
          </button>

          <button
            onClick={() => setActiveTab('trades')}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              activeTab === 'trades'
                ? 'bg-slate-800 text-cyan-400 border border-slate-700'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
            }`}
          >
            <History className="w-3.5 h-3.5" />
            <span>Trade History ({trades.length})</span>
          </button>

          <button
            onClick={() => setActiveTab('signals')}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              activeTab === 'signals'
                ? 'bg-slate-800 text-cyan-400 border border-slate-700'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
            }`}
          >
            <Zap className="w-3.5 h-3.5" />
            <span>AI Signals</span>
          </button>

          <button
            onClick={() => setActiveTab('settings')}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              activeTab === 'settings'
                ? 'bg-slate-800 text-cyan-400 border border-slate-700'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
            }`}
          >
            <Settings className="w-3.5 h-3.5" />
            <span>Risk Settings</span>
          </button>

          <button
            onClick={() => setActiveTab('logs')}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              activeTab === 'logs'
                ? 'bg-slate-800 text-cyan-400 border border-slate-700'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
            }`}
          >
            <Terminal className="w-3.5 h-3.5" />
            <span>Bot Terminal Logs</span>
          </button>
        </div>
      </header>

      {/* Main Content Body */}
      <main className="flex-1 max-w-7xl w-full mx-auto p-4 space-y-6">
        {/* Metric Cards Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Card 1: Balance */}
          <div className="glass-card rounded-xl p-4 border border-slate-800">
            <div className="flex justify-between items-center text-slate-400 text-xs mb-1">
              <span>Account Balance</span>
              <DollarSign className="w-4 h-4 text-cyan-400" />
            </div>
            <div className="text-2xl font-bold font-mono text-white">
              ${(accountBalance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Market: <span className="text-slate-300 font-mono">{settings?.selected_market || 'R_100'}</span>
            </div>
          </div>

          {/* Card 2: Today's P&L */}
          <div className="glass-card rounded-xl p-4 border border-slate-800">
            <div className="flex justify-between items-center text-slate-400 text-xs mb-1">
              <span>Today's Profit / Loss</span>
              {isPnLPositive ? (
                <TrendingUp className="w-4 h-4 text-emerald-400" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-400" />
              )}
            </div>
            <div
              className={`text-2xl font-bold font-mono ${
                isPnLPositive ? 'text-emerald-400' : 'text-red-400'
              }`}
            >
              {isPnLPositive ? '+' : ''}${(todayPnL || 0).toFixed(2)}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Daily Risk Limit: <span className="text-amber-400 font-mono">{settings?.daily_loss_limit}%</span>
            </div>
          </div>

          {/* Card 3: Win Rate */}
          <div className="glass-card rounded-xl p-4 border border-slate-800">
            <div className="flex justify-between items-center text-slate-400 text-xs mb-1">
              <span>Win Rate</span>
              <Award className="w-4 h-4 text-amber-400" />
            </div>
            <div className="text-2xl font-bold font-mono text-white">
              {(winRate || 0).toFixed(1)}%
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Total Trades Today: <span className="text-slate-300 font-mono">{tradeCount}</span>
            </div>
          </div>

          {/* Card 4: Bot Control Action Bar */}
          <div className="glass-card rounded-xl p-4 border border-slate-800 flex flex-col justify-between">
            <div className="flex justify-between items-center text-slate-400 text-xs mb-2">
              <span>Automated Engine</span>
              <span
                className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${
                  isRunning
                    ? 'bg-emerald-950 text-emerald-400 border border-emerald-800'
                    : 'bg-slate-800 text-slate-400'
                }`}
              >
                {botStatus}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={handleStartStop}
                disabled={isProcessing}
                className={`py-2 px-3 rounded-lg font-bold text-xs flex items-center justify-center space-x-1.5 transition-all shadow-md ${
                  isRunning
                    ? 'bg-amber-600 hover:bg-amber-500 text-white shadow-amber-600/20'
                    : 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-emerald-600/20'
                }`}
              >
                {isRunning ? (
                  <>
                    <Square className="w-3.5 h-3.5 fill-current" />
                    <span>PAUSE BOT</span>
                  </>
                ) : (
                  <>
                    <Play className="w-3.5 h-3.5 fill-current" />
                    <span>START BOT</span>
                  </>
                )}
              </button>

              <button
                onClick={handleEmergency}
                disabled={isProcessing}
                className="py-2 px-3 rounded-lg font-bold text-xs bg-red-600 hover:bg-red-500 text-white flex items-center justify-center space-x-1 transition-all shadow-md shadow-red-600/20"
                title="Halt all trades and disconnect immediately"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                <span>HALT</span>
              </button>
            </div>
          </div>
        </div>

        {/* Bot start error */}
        {botError && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-500/40 bg-red-950/30 px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-red-300">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>{botError}</span>
            </div>
            <button
              onClick={() => setBotError(null)}
              className="px-3 py-1.5 rounded-lg bg-red-500/20 text-red-300 border border-red-500/40 hover:bg-red-500/30 text-xs font-semibold transition-colors"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Deriv not connected banner */}
        {!derivAccount?.loginid && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/40 bg-amber-950/30 px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-amber-300">
              <Link2 className="w-4 h-4 flex-shrink-0" />
              <span>Your Deriv account isn&apos;t connected yet — balance and trading are disabled until you connect it.</span>
            </div>
            <button
              onClick={() => setActiveTab('settings')}
              className="px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30 text-xs font-semibold transition-colors whitespace-nowrap"
            >
              Connect Deriv Account
            </button>
          </div>
        )}

        {/* Tab 1: Dashboard Primary Layout */}
        {activeTab === 'dashboard' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left 2 Cols: Current AI Signal & Open Positions */}
            <div className="lg:col-span-2 space-y-6">
              {/* Current Signal Card */}
              <div className="glass-card rounded-xl p-5 border border-slate-800">
                <div className="flex justify-between items-center border-b border-slate-800 pb-3 mb-4">
                  <div className="flex items-center space-x-2">
                    <Cpu className="w-5 h-5 text-cyan-400 animate-pulse" />
                    <h2 className="text-sm font-bold text-white uppercase tracking-wider">
                      Latest AI Analysis Signal
                    </h2>
                  </div>
                  <span className="text-xs font-mono text-slate-400">
                    {currentSignal?.created_at ? new Date(currentSignal.created_at).toLocaleTimeString() : 'Live'}
                  </span>
                </div>

                {currentSignal ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
                    {/* Signal Decision Badge */}
                    <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 text-center">
                      <span className="text-[11px] text-slate-400 block mb-1">Decision</span>
                      <span
                        className={`text-2xl font-black font-mono tracking-wider ${
                          currentSignal.decision === 'BUY'
                            ? 'text-emerald-400 text-glow-green'
                            : currentSignal.decision === 'SELL'
                            ? 'text-red-400 text-glow-red'
                            : 'text-slate-400'
                        }`}
                      >
                        {currentSignal.decision}
                      </span>
                      <span className="text-xs text-slate-500 block mt-1 font-mono">
                        Market: {currentSignal.market}
                      </span>
                    </div>

                    {/* Confidence Meter */}
                    <div className="p-4 rounded-xl bg-slate-900 border border-slate-800">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-400">AI Confidence</span>
                        <span className="text-cyan-400 font-mono font-bold">
                          {currentSignal.confidence}%
                        </span>
                      </div>
                      <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-cyan-500 to-emerald-400 rounded-full transition-all duration-500"
                          style={{ width: `${currentSignal.confidence}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-[10px] text-slate-500 mt-2 font-mono">
                        <span>Threshold: {settings?.confidence_threshold || 75}%</span>
                        <span>Risk: {currentSignal.risk_level}</span>
                      </div>
                    </div>

                    {/* Reason */}
                    <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 sm:col-span-1">
                      <span className="text-[11px] text-slate-400 block mb-1">AI Reasoning</span>
                      <p className="text-xs text-slate-300 line-clamp-3 italic">
                        "{currentSignal.reason || 'Sufficient momentum and technical confluence detected.'}"
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="py-8 text-center text-slate-500 text-xs">
                    Scanning market feeds... Awaiting next signal evaluation cycle.
                  </div>
                )}
              </div>

              {/* Open Positions Card */}
              <div className="glass-card rounded-xl p-5 border border-slate-800">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center space-x-2">
                    <Activity className="w-4 h-4 text-emerald-400" />
                    <h2 className="text-sm font-bold text-white uppercase tracking-wider">
                      Active Positions ({openPositions.length})
                    </h2>
                  </div>
                  <span className="text-xs text-slate-400 font-mono">
                    Max Allowed: {settings?.max_open_trades || 2}
                  </span>
                </div>

                {openPositions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-900/80 text-slate-400 uppercase font-mono text-[10px]">
                        <tr>
                          <th className="p-2.5">Market</th>
                          <th className="p-2.5">Direction</th>
                          <th className="p-2.5">Entry Price</th>
                          <th className="p-2.5">Stake</th>
                          <th className="p-2.5">Current P&L</th>
                          <th className="p-2.5 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800 font-mono">
                        {openPositions.map((pos) => {
                          const pnl = pos.current_profit_loss || 0;
                          return (
                            <tr key={pos.id} className="hover:bg-slate-900/50">
                              <td className="p-2.5 font-bold text-white">{pos.market}</td>
                              <td className="p-2.5">
                                <span
                                  className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                    pos.direction === 'BUY' || pos.direction === 'CALL'
                                      ? 'bg-emerald-950 text-emerald-400 border border-emerald-800'
                                      : 'bg-red-950 text-red-400 border border-red-800'
                                  }`}
                                >
                                  {pos.direction}
                                </span>
                              </td>
                              <td className="p-2.5 text-slate-300">${(pos.entry_price || 0).toFixed(2)}</td>
                              <td className="p-2.5 text-slate-300">${(pos.stake || 0).toFixed(2)}</td>
                              <td
                                className={`p-2.5 font-bold ${
                                  pnl >= 0 ? 'text-emerald-400' : 'text-red-400'
                                }`}
                              >
                                {pnl >= 0 ? '+' : ''}${(pnl || 0).toFixed(2)}
                              </td>
                              <td className="p-2.5 text-right">
                                <button
                                  onClick={() => closePosition(pos.id)}
                                  className="px-2 py-1 bg-red-950 hover:bg-red-900 text-red-300 border border-red-800 rounded text-[10px] font-bold transition-colors"
                                >
                                  Close
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-8 text-center bg-slate-900/50 rounded-xl border border-slate-800/50 text-slate-500 text-xs">
                    No open positions at this moment. Bot will enter on high-confidence signal.
                  </div>
                )}
              </div>
            </div>

            {/* Right Col: Parameters Summary & Quick Strategy Overview */}
            <div className="space-y-6">
              <div className="glass-card rounded-xl p-5 border border-slate-800">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-300 mb-4 border-b border-slate-800 pb-2">
                  Active Risk Controls
                </h3>
                <div className="space-y-3 text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Risk per Trade</span>
                    <span className="font-mono text-emerald-400 font-semibold">
                      {settings?.risk_per_trade || 0.5}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Daily Loss Limit</span>
                    <span className="font-mono text-amber-400 font-semibold">
                      {settings?.daily_loss_limit || 2.0}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Max Consecutive Losses</span>
                    <span className="font-mono text-slate-200">
                      {settings?.max_consecutive_losses || 3}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Martingale Protection</span>
                    <span className="font-mono px-2 py-0.5 bg-emerald-950 border border-emerald-800 text-emerald-400 rounded text-[10px] font-bold">
                      DISABLED (Safe)
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Fallback Strategy</span>
                    <span className="font-mono text-cyan-400">Rule-Based Active</span>
                  </div>
                </div>
              </div>

              {/* Deriv Connection Info Card */}
              <div className="glass-card rounded-xl p-5 border border-slate-800">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-300 mb-3 border-b border-slate-800 pb-2">
                  Deriv Integration
                </h3>
                <p className="text-xs text-slate-400 mb-4 leading-relaxed">
                  Connected directly to Deriv WebSockets for instant sub-second contract execution.
                </p>
                <a
                  href="https://app.deriv.com"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center space-x-1.5 text-xs text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
                >
                  <span>Open Deriv Portal</span>
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Trades History */}
        {activeTab === 'trades' && (
          <div className="glass-card rounded-xl p-5 border border-slate-800">
            <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-4">
              Historical Trade Logs
            </h2>
            {trades.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-900 text-slate-400 uppercase font-mono text-[10px]">
                    <tr>
                      <th className="p-3">Time</th>
                      <th className="p-3">Market</th>
                      <th className="p-3">Direction</th>
                      <th className="p-3">Stake</th>
                      <th className="p-3">Payout</th>
                      <th className="p-3">Result</th>
                      <th className="p-3">P&L</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800 font-mono">
                    {trades.map((t) => (
                      <tr key={t.id} className="hover:bg-slate-900/50">
                        <td className="p-3 text-slate-400">
                          {new Date(t.opened_at).toLocaleTimeString()}
                        </td>
                        <td className="p-3 text-white font-bold">{t.market}</td>
                        <td className="p-3">{t.direction}</td>
                        <td className="p-3">${(t.stake || 0).toFixed(2)}</td>
                        <td className="p-3">${(t.payout || 0).toFixed(2)}</td>
                        <td className="p-3">
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${
                              t.result === 'win'
                                ? 'bg-emerald-950 text-emerald-400 border border-emerald-800'
                                : 'bg-red-950 text-red-400 border border-red-800'
                            }`}
                          >
                            {t.result}
                          </span>
                        </td>
                        <td
                          className={`p-3 font-bold ${
                            (t.profit_loss || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                          }`}
                        >
                          {(t.profit_loss || 0) >= 0 ? '+' : ''}
                          ${(t.profit_loss || 0).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-slate-500 p-8 text-center">
                No recent trades recorded yet.
              </p>
            )}
          </div>
        )}

        {/* Tab 3: AI Signals */}
        {activeTab === 'signals' && (
          <div className="glass-card rounded-xl p-5 border border-slate-800">
            <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-4">
              AI Market Intelligence Feed
            </h2>
            <p className="text-xs text-slate-400 mb-4">
              Real-time deep learning & multi-timeframe indicator analysis evaluations.
            </p>
            {currentSignal ? (
              <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 text-xs space-y-2">
                <div className="flex justify-between font-mono">
                  <span className="text-cyan-400 font-bold">{currentSignal.market}</span>
                  <span className="text-slate-400">{new Date(currentSignal.created_at).toLocaleString()}</span>
                </div>
                <div className="text-slate-200">{currentSignal.reason}</div>
              </div>
            ) : (
              <p className="text-xs text-slate-500">No signals generated yet.</p>
            )}
          </div>
        )}

        {/* Tab 4: Risk Settings */}
        {activeTab === 'settings' && (
          <div className="max-w-2xl mx-auto space-y-6">
            <DerivConnect />

          <div className="glass-card rounded-xl p-6 border border-slate-800 space-y-6">
            <h2 className="text-sm font-bold text-white uppercase tracking-wider border-b border-slate-800 pb-3">
              Trading & Risk Configuration
            </h2>

            <div className="space-y-4 text-xs">
              <div>
                <label className="block text-slate-300 font-medium mb-1">
                  Risk Per Trade (% of Account Balance)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={settings?.risk_per_trade || 0.5}
                  onChange={(e) => updateSettings({ risk_per_trade: parseFloat(e.target.value) })}
                  className="w-full p-2.5 bg-slate-900 border border-slate-800 rounded text-slate-100 font-mono"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-medium mb-1">
                  Daily Loss Limit (% Hard Cutoff)
                </label>
                <input
                  type="number"
                  step="0.5"
                  value={settings?.daily_loss_limit || 2.0}
                  onChange={(e) => updateSettings({ daily_loss_limit: parseFloat(e.target.value) })}
                  className="w-full p-2.5 bg-slate-900 border border-slate-800 rounded text-slate-100 font-mono"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-medium mb-1">
                  AI Confidence Cutoff Threshold (0 - 100%)
                </label>
                <input
                  type="number"
                  value={settings?.confidence_threshold || 75}
                  onChange={(e) => updateSettings({ confidence_threshold: parseInt(e.target.value) })}
                  className="w-full p-2.5 bg-slate-900 border border-slate-800 rounded text-slate-100 font-mono"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-medium mb-1">Target Market</label>
                <select
                  value={settings?.selected_market || 'R_100'}
                  onChange={(e) => updateSettings({ selected_market: e.target.value })}
                  className="w-full p-2.5 bg-slate-900 border border-slate-800 rounded text-slate-100 font-mono"
                >
                  <option value="R_100">Volatility 100 Index (R_100)</option>
                  <option value="R_75">Volatility 75 Index (R_75)</option>
                  <option value="R_50">Volatility 50 Index (R_50)</option>
                  <option value="R_25">Volatility 25 Index (R_25)</option>
                  <option value="R_10">Volatility 10 Index (R_10)</option>
                </select>
              </div>
            </div>
          </div>
          </div>
        )}

        {/* Tab 5: Terminal Logs */}
        {activeTab === 'logs' && (
          <div className="glass-card rounded-xl p-5 border border-slate-800">
            <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-3">
              Worker Runtime Console Logs
            </h2>
            <div className="bg-slate-950 p-4 rounded-xl font-mono text-xs text-slate-300 max-h-96 overflow-y-auto space-y-1.5 border border-slate-900">
              {botLogs.length > 0 ? (
                botLogs.map((log) => (
                  <div key={log.id} className="flex space-x-3">
                    <span className="text-slate-500">[{new Date(log.created_at).toLocaleTimeString()}]</span>
                    <span
                      className={`font-bold uppercase ${
                        log.level === 'error'
                          ? 'text-red-400'
                          : log.level === 'warn'
                          ? 'text-amber-400'
                          : 'text-cyan-400'
                      }`}
                    >
                      [{log.level}]
                    </span>
                    <span>{log.message}</span>
                  </div>
                ))
              ) : (
                <div className="text-slate-500">
                  [SYSTEM] Engine online. Awaiting system logs...
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
